(function () {
  "use strict";

  const MAX_INITIALIZATION_RETRIES = 10;

  let ongoingCheckPromise = null;
  let isInitializing = false;
  let isInitialized = false;
  let initializationRetryCount = 0;

  function init() {
    if (isInitializing || isInitialized) {
      return;
    }

    isInitializing = true;
    initializationRetryCount++;

    if (typeof ProductForm === "undefined" || !window.boaBranchPicker) {
      retryInit();
      return;
    }

    setupInterceptors();
    isInitialized = true;
    isInitializing = false;
  }

  function retryInit() {
    if (initializationRetryCount >= MAX_INITIALIZATION_RETRIES) {
      isInitializing = false;
      return;
    }

    setTimeout(() => {
      isInitializing = false;
      init();
    }, 100);
  }

  function setupInterceptors() {
    const originalProductFormSubmit = ProductForm.prototype._onSubmit;

    // Dual interception: document listener (capture) + prototype override (backup)
    // Document listener fires first and stops propagation, preventing double handling
    document.addEventListener(
      "submit",
      async function (event) {
        const form = event.target;

        if (
          !(form instanceof HTMLFormElement) ||
          form.getAttribute("is") !== "product-form"
        ) {
          return;
        }

        // Stop propagation to prevent form's bound listener from firing
        event.stopPropagation();
        await handleFormSubmit(form, originalProductFormSubmit, event);
      },
      true
    );

    // Prototype override catches dynamically created forms (backup mechanism)
    // Guard prevents double processing if document listener didn't catch it
    ProductForm.prototype._onSubmit = async function (event) {
      if (event.defaultPrevented) {
        return;
      }

      await handleFormSubmit(this, originalProductFormSubmit, event);
    };

    // Override vase bundle cart method to ensure branch selection
    const VaseBundleManagerClass = customElements.get("vase-bundle-carousel");
    const originalAddBundleToCart =
      VaseBundleManagerClass?.prototype?.addBundleToCart;

    // Only override if method exists to prevent TypeError
    if (
      VaseBundleManagerClass &&
      typeof originalAddBundleToCart === "function"
    ) {
      VaseBundleManagerClass.prototype.addBundleToCart = async function () {
        if (!this.selectedBundle) {
          return originalAddBundleToCart.call(this);
        }

        const cart = await ensureBranchSelected();
        if (!cart) {
          showBranchPickerError(
            new Error("Branch picker closed without selection")
          );
          return;
        }

        // Variant ids come from the selected bundle, not a positional form lookup
        // (the carousel sits outside the product form on the PDP).
        if (isBundleReserved(this, cart)) {
          await handleReservedBlock(cart);
          return;
        }

        // The flower is the gated PDP product; block if it is not in the special catalog.
        if (isSpecialDateBlocked(this.productForm, cart)) {
          await handleSpecialDateBlock(cart);
          return;
        }

        return originalAddBundleToCart.call(this);
      };
    }
  }

  // Flower reserve context may use the carousel's bound productForm; vase days/tags
  // come from selectedBundle (written at selection time from the vase cell).
  function isBundleReserved(bundleManager, cart) {
    const api = window.BoaCatalogPopup;
    if (
      !api ||
      typeof api.isVariantsReservedForCart !== "function"
    ) {
      return false;
    }

    const bundle = bundleManager.selectedBundle;
    if (!bundle) {
      return false;
    }

    const variants = [];

    if (bundle.flowerId != null && bundle.flowerId !== "") {
      const flowerForm = bundleManager.productForm;
      const flowerContext =
        flowerForm && typeof api.getFormReserveContext === "function"
          ? api.getFormReserveContext(flowerForm)
          : { reserveMap: {}, productTags: [] };
      variants.push({
        variantId: bundle.flowerId,
        productTags: flowerContext.productTags,
        reserveMap: flowerContext.reserveMap,
      });
    }

    if (bundle.vaseId != null && bundle.vaseId !== "") {
      variants.push({
        variantId: bundle.vaseId,
        productTags: Array.isArray(bundle.vaseProductTags)
          ? bundle.vaseProductTags
          : [],
        days: bundle.vaseReserveDays,
        title: bundle.vaseVariantTitle || "",
      });
    }

    return Boolean(api.isVariantsReservedForCart(variants, cart));
  }

  async function handleFormSubmit(form, originalSubmit, event) {
    event.preventDefault();

    const vaseBundleManager =
      form.closest("vase-bundle-carousel") ||
      document.querySelector("vase-bundle-carousel");

    if (vaseBundleManager?.selectedBundle) {
      return vaseBundleManager.addBundleToCart();
    }

    const cart = await ensureBranchSelected();
    if (!cart) {
      return;
    }

    if (isFormReserved(form, cart)) {
      await handleReservedBlock(cart);
      return;
    }

    // In-period special-date catalog gate. The server render + button-state gate key
    // off the special_date_tag cart attribute, which is written only AFTER a date is
    // picked — so when the user picks the special date as part of this add flow, the
    // add can race ahead of the gate. Enforce membership here, the actual chokepoint.
    if (isSpecialDateBlocked(form, cart)) {
      await handleSpecialDateBlock(cart);
      return;
    }

    await injectBranchLineItemProperties(form, cart);
    return originalSubmit.call(form, event);
  }

  function isFormReserved(form, cart) {
    return Boolean(
      window.BoaCatalogPopup &&
        typeof window.BoaCatalogPopup.isFormReservedForCart === "function" &&
        window.BoaCatalogPopup.isFormReservedForCart(form, cart)
    );
  }

  async function handleReservedBlock(cart) {
    showReservedCartError();
    await syncCatalogUi(cart);
  }

  function isSpecialDateBlocked(form, cart) {
    return Boolean(
      window.BoaCatalogPopup &&
        typeof window.BoaCatalogPopup.isFormBlockedBySpecialDate === "function" &&
        window.BoaCatalogPopup.isFormBlockedBySpecialDate(form, cart)
    );
  }

  async function handleSpecialDateBlock(cart) {
    showSpecialDateError();
    // Syncing writes special_date_tag and swaps the buy button for the catalog link.
    await syncCatalogUi(cart);
  }

  async function syncCatalogUi(cart) {
    const syncUi =
      window.BoaCatalogPopup?.handleBranchSelected ||
      window.boaCatalogPopupHandleBranchSelected;
    if (typeof syncUi === "function") {
      try {
        await syncUi({
          detail: { cart },
        });
      } catch (error) {
        // UI sync is best-effort; blocking the add is the critical part.
      }
    }
  }

  // Injects the current branch/delivery line-item properties into the add-to-cart
  // form so a repeat add carries the same hidden properties the cart later stamps
  // onto every line. Without this the new line's properties differ from existing
  // lines and Shopify creates a duplicate line instead of incrementing quantity.
  async function injectBranchLineItemProperties(form, cart) {
    // Clear previously injected inputs so values reflect the current cart
    form
      .querySelectorAll("input[data-boa-branch-property]")
      .forEach((input) => input.remove());

    // Bundle adds use items[] syntax (different property association) - skip
    if (!form.querySelector('input[name="id"]')) {
      return;
    }

    const cartUtils = window.BoaBranchPicker?.cartUtils;
    if (!cartUtils?.buildLineItemProperties) {
      return;
    }

    const effectiveCart = cart || (await fetchCart());
    if (!effectiveCart?.attributes?.address_type) {
      return;
    }

    const properties = cartUtils.buildLineItemProperties(
      effectiveCart.attributes
    );
    for (const [key, value] of Object.entries(properties)) {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = `properties[${key}]`;
      input.value = value;
      input.setAttribute("data-boa-branch-property", "");
      form.appendChild(input);
    }
  }

  async function ensureBranchSelected() {
    if (ongoingCheckPromise) {
      return ongoingCheckPromise;
    }

    ongoingCheckPromise = (async () => {
      try {
        let cart = await fetchCart();
        if (!hasValidDeliverySelection(cart)) {
          cart = await waitForBranchPickerSelection();
        }
        return hasValidDeliverySelection(cart) ? cart : null;
      } catch (error) {
        showBranchPickerError(error);
        return null;
      } finally {
        ongoingCheckPromise = null;
      }
    })();

    return ongoingCheckPromise;
  }

  async function fetchCart() {
    try {
      const cartUrl = `${window.themeVariables?.routes?.cartUrl || "/cart"}.js`;
      const response = await fetch(cartUrl, {
        credentials: "same-origin",
        cache: "no-store",
        headers: { Accept: "application/json" },
      });

      if (!response.ok) {
        return null;
      }

      return await response.json();
    } catch (error) {
      return null;
    }
  }

  function hasValidDeliverySelection(cart) {
    const attributes = cart?.attributes || {};
    const deliveryDate = (attributes.delivery_date || "").trim();
    const branchVerifoneId = (attributes.branch_verifone_id || "").trim();
    const addressType = (attributes.address_type || "").trim();
    return Boolean(deliveryDate && branchVerifoneId && addressType);
  }

  function waitForBranchPickerSelection() {
    return new Promise((resolve, reject) => {
      if (!window.boaBranchPicker) {
        reject(new Error("Branch picker not available"));
        return;
      }

      const branchPickerModal = document.getElementById("boa-branch-picker");
      if (!branchPickerModal) {
        reject(new Error("Branch picker modal not found"));
        return;
      }

      let closeObserver;
      let promiseResolved = false;

      function selectionHandler(event) {
        if (promiseResolved) {
          return;
        }

        const cart = event.detail?.cart;
        if (!hasValidDeliverySelection(cart)) {
          return;
        }

        promiseResolved = true;
        cleanupBranchPickerHandlers({
          closeObserver,
          handlerRef: selectionHandler,
        });
        resolve(cart);
      }

      // Monitor modal close to check cart state if user closes without explicit selection
      closeObserver = new MutationObserver(() => {
        if (branchPickerModal.hasAttribute("open") || promiseResolved) {
          return;
        }
        promiseResolved = true;
        checkCartAfterClose({
          closeObserver,
          handlerRef: selectionHandler,
          resolve,
          reject,
        });
      });

      // branch:selected carries the updated cart; cart:updated is intentionally omitted
      // here — unrelated cart updates (e.g. bundle qty sync) must not lock promiseResolved.
      document.addEventListener("branch:selected", selectionHandler, {
        once: true,
      });

      closeObserver.observe(branchPickerModal, {
        attributes: true,
        attributeFilter: ["open"],
      });

      if (!branchPickerModal.hasAttribute("open")) {
        window.boaBranchPicker.open();
      }
    });
  }

  // Cleanup: disconnect observer and remove listeners
  // Defensive cleanup even though listeners use { once: true }
  function cleanupBranchPickerHandlers(options) {
    options.closeObserver?.disconnect();
    document.removeEventListener("branch:selected", options.handlerRef);
  }

  // Hybrid retry/delay: waits for cart:updated event, then retries if needed
  // Handles timing issues when modal closes quickly after branch selection
  async function checkCartAfterClose(options) {
    // Wait for cart:updated event (1.5s timeout) - handles event-based completion
    const cartUpdated = await new Promise((resolve) => {
      const timeout = setTimeout(() => resolve(false), 1500);

      const handler = () => {
        clearTimeout(timeout);
        document.removeEventListener("cart:updated", handler);
        resolve(true);
      };

      document.addEventListener("cart:updated", handler, { once: true });
    });

    // If event fired, check once; otherwise retry up to 3 times with delays
    let cart = null;
    const maxAttempts = cartUpdated ? 1 : 3;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      if (attempt > 0) {
        await new Promise((resolve) => setTimeout(resolve, 300));
      }

      cart = await fetchCart();
      if (hasValidDeliverySelection(cart)) {
        break;
      }
      cart = null;
    }

    cleanupBranchPickerHandlers({
      closeObserver: options.closeObserver,
      handlerRef: options.handlerRef,
    });

    if (cart) {
      options.resolve(cart);
    } else {
      options.reject(new Error("Branch picker closed without selection"));
    }
  }

  function showBranchPickerError(error) {
    const defaultMessage =
      window.themeVariables?.strings?.branchPickerErrorSelectBranch ||
      "Please select a branch to continue";

    const errorMessage =
      error.message === "Branch picker closed without selection"
        ? defaultMessage
        : error.message || defaultMessage;

    document.documentElement.dispatchEvent(
      new CustomEvent("cart-notification:show", {
        bubbles: true,
        cancelable: true,
        detail: {
          status: "error",
          error: errorMessage,
        },
      })
    );
  }

  function showReservedCartError() {
    const reservedMessage =
      window.themeVariables?.strings?.specialCatalogReserved ||
      "This product is reserved for a special delivery date.";

    document.documentElement.dispatchEvent(
      new CustomEvent("cart-notification:show", {
        bubbles: true,
        cancelable: true,
        detail: {
          status: "error",
          error: reservedMessage,
        },
      })
    );
  }

  function showSpecialDateError() {
    const message =
      window.themeVariables?.strings?.specialCatalogNotAvailable ||
      "This product is not available for the selected delivery date.";

    document.documentElement.dispatchEvent(
      new CustomEvent("cart-notification:show", {
        bubbles: true,
        cancelable: true,
        detail: {
          status: "error",
          error: message,
        },
      })
    );
  }

  window.BoaCartInterceptor = {
    ensureBranchSelected,
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
